import { useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  Tabs, Tab, Box, AppBar, Toolbar, Typography, IconButton,
  Snackbar, Alert, Button, Stack, Menu, MenuItem, Avatar, Divider, Tooltip
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import HomeIcon from "@mui/icons-material/Home";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { RiLogoutCircleRLine } from "react-icons/ri";

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null); // For profile menu
  const employeeDetails = JSON.parse(localStorage.getItem("employeeDetails"));

  const tabPaths = ["/sidebar/history", "/sidebar/home", "/sidebar/employeeDetails"];
  const currentTab = tabPaths.indexOf(location.pathname);

  const handleChange = (event, newValue) => {
    const basePath = tabPaths[newValue];
    if (basePath === "/sidebar/home") {
      // Pass key via query param
      navigate(`${basePath}`);
    } else {
      navigate(basePath);
    }
  };

  const handleLogout = () => setSnackbarOpen(true);
  const confirmLogout = () => {
    localStorage.removeItem("employeeDetails");
    navigate("/", { replace: true });
  };
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const handleProfileClose = () => setAnchorEl(null);

  const initials = employeeDetails?.name
    ? employeeDetails.name.split(' ').map(n => n[0]).join('')
    : 'G';

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <AppBar position="static" color="none" sx={{ px: 2, bgcolor: "#e8f5e9" }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600, color: '#2E7D32', textTransform: "uppercase" }}>
            Staff Portal
          </Typography>
          <Tooltip title="Logout">
            <IconButton onClick={handleLogout} sx={{ color: '#eb8f2d' }}>
              <RiLogoutCircleRLine size={20} />
              <Typography
                variant="body2"
                sx={{ fontSize: '0.75rem', ml: 0.5, color: '#eb8f2d', fontWeight: "bold" }}
              >
                LOGOUT
              </Typography>
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileClose}
        PaperProps={{
          sx: {
            p: 1.5,
            borderRadius: 2,
            boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
            minWidth: 220,
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Avatar sx={{ bgcolor: '#15830D', mr: 1.5 }}>{initials}</Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {employeeDetails?.name || "Guest"}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {employeeDetails?.email || "N/A"}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <MenuItem disableRipple sx={{ "&:hover": { bgcolor: '#e8f5e9' } }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Emp ID: {employeeDetails?.emp_id || "N/A"}
          </Typography>
        </MenuItem>

        {/* Update Password Link */}
        <MenuItem disableRipple sx={{ justifyContent: 'flex-end', "&:hover": { bgcolor: '#e8f5e9' } }}>
          <a href="update-password" className="text-sm text-[#15830D] hover:underline font-medium">
            Update Password?
          </a>
        </MenuItem>

      </Menu>

      {/* Main Content */}
      <Box sx={{
        flexGrow: 1,
        overflow: "auto",
        p: 2,
        pb: 8,
        alignItems: "center",
        backgroundColor: "#fafafa",
        justifyContent: "center",
      }}>
        <Outlet />
      </Box>

      {/* Tabs Navigation */}
      <Tabs
        value={currentTab === -1 ? 1 : currentTab}
        onChange={handleChange}
        centered
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: "#e8f5e9",
          borderTop: "1px solid #ccc",
          zIndex: 1100,
          "& .MuiTabs-indicator": {
            backgroundColor: "#15830D",
          },
        }}
      >
        <Tab
          icon={<HistoryIcon />}
          label="History"
          sx={{
            "&.Mui-selected": {
              color: "#15830D",
              "& .MuiSvgIcon-root": {
                color: "#15830D",
              },
            },
          }}
        />
        <Tab
          icon={<HomeIcon />}
          label="Home"
          sx={{
            mx: 4,
            "&.Mui-selected": {
              color: "#15830D",
              "& .MuiSvgIcon-root": {
                color: "#15830D",
              },
            },
          }}
        />
        <Tab
          icon={<AccountCircleIcon />}
          label="Profile"
          sx={{
            "&.Mui-selected": {
              color: "#15830D",
              "& .MuiSvgIcon-root": {
                color: "#15830D",
              },
            },
          }}
        />
      </Tabs>


      {/* Snackbar with YES/NO */}
      <Snackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          sx={{ width: '100%', display: 'flex', alignItems: 'center', fontWeight: 500 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained" size="small" color="success"
                onClick={confirmLogout} sx={{ textTransform: 'none' }}
              >
                Yes
              </Button>
              <Button
                variant="outlined" size="small" color="error"
                onClick={handleSnackbarClose} sx={{ textTransform: 'none' }}
              >
                No
              </Button>
            </Stack>
          }
        >
          Are you sure you want to logout?
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Sidebar;
